import { UserType } from '@/types';
import { Access, Roles } from '@/utils/roles';
import { CustomerId, InstitutionId, OrganizationId } from '@qmrs/entityid';
import { defineStore } from 'pinia';

export const useSessionContextStore = defineStore('sessionContext', () => {
    const sessionToken = ref<SessionToken | undefined>();
    const roles = ref<Roles>(new Roles([]));
    const userId = ref<string | undefined>();
    const type = ref(UserType.None);
    const name = ref('');
    const access = ref<Access | undefined>();
    const contextId = ref<
        CustomerId | OrganizationId | InstitutionId | undefined
    >();
    const federalStateId = ref<number | undefined>();
    const federalStateBranchId = ref<number | undefined>();

    const isAdmin = computed(() => type.value === UserType.Admin);
    const isAdminOrSupport = computed(
        () => type.value === UserType.Admin || type.value === UserType.Support
    );

    async function init(token: SessionToken) {
        sessionToken.value = token;
        userId.value = token.sub;
        roles.value = new Roles(token.realm_access.roles);
        const hasRole = (role: string) => roles.value.hasRealmRole(role);
        access.value = roles.value.access();
        if (hasRole('administration')) {
            type.value = UserType.Admin;
        } else if (hasRole('support')) {
            type.value = UserType.Support;
        } else if (access.value) {
            let ty = UserType.None;
            if (hasRole('olb')) {
                if (hasRole('branch')) {
                    ty = UserType.OlbBranch;
                } else {
                    ty = UserType.Olb;
                }
            } else if (hasRole('hdp')) {
                ty = UserType.Hdp;
            } else if (hasRole('lab')) {
                if (hasRole('branch')) {
                    ty = UserType.LabBranch;
                } else {
                    ty = UserType.Lab;
                }
            } else if (hasRole('wsp')) {
                if (hasRole('branch')) {
                    ty = UserType.WspBranch;
                } else {
                    ty = UserType.Wsp;
                }
            }

            if (access.value.enforce(ty)) {
                type.value = ty;
                contextId.value = access.value.contextId;
                if (contextId.value instanceof CustomerId) {
                    federalStateId.value = Number(contextId.value._id);
                }
                if (contextId.value instanceof OrganizationId) {
                    federalStateId.value = Number(contextId.value._cid);
                    if (ty === UserType.OlbBranch) {
                        federalStateBranchId.value = Number(
                            contextId.value._id
                        );
                    }
                }
                if (contextId.value instanceof InstitutionId) {
                    federalStateId.value = Number(contextId.value._cid);
                    if (ty === UserType.OlbBranch) {
                        federalStateBranchId.value = Number(
                            contextId.value._id
                        );
                    }
                    if (ty === UserType.Hdp) {
                        federalStateBranchId.value = Number(
                            contextId.value._oid
                        );
                    }
                }
            }
        } else if (hasRole('applicant')) {
            type.value = UserType.Applicant;
        }
    }

    const fullname = computed(
        () =>
            `${sessionToken.value?.given_name} ${sessionToken.value?.family_name}`
    );

    return {
        init,
        sessionToken,
        isAdminOrSupport,
        isAdmin,
        roles,
        type,
        fullname,
        userId,
        contextId,
        name,
        federalStateId,
        federalStateBranchId
    };
});
